import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  formatQueryParams,
  JsonApiResponseDto,
  parseJsonApiResponse,
  prepareHeaders,
  Response,
  TQueryParams,
} from 'evolve-ui';

export type TProvider = {
  id: string;
  name: string;
  active: boolean;
};

export type TCreateProviderBody = {
  name: string;
  active: boolean;
};

export type TUpdateProviderBody = Partial<TCreateProviderBody>;

export const providersApi = createApi({
  reducerPath: 'providers',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.GATSBY_PROVIDERS_API_URL,
    mode: 'cors',
    prepareHeaders,
  }),
  tagTypes: ['Provider'],
  endpoints: (builder) => ({
    getProviders: builder.query<Response<TProvider>, TQueryParams>({
      providesTags: ['Provider'],
      query: (params) => formatQueryParams(params),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TProvider>(
          res as JsonApiResponseDto<TProvider>,
          meta,
          args,
          true,
        ),
    }),
    getProvider: builder.query<Response<TProvider>, { id: string }>({
      providesTags: ['Provider'],
      query: ({ id }) => ({
        url: `${id}`,
        method: 'GET',
      }),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TProvider>(
          res as JsonApiResponseDto<TProvider>,
          meta,
          args,
          true,
        ),
    }),
    createProvider: builder.mutation<
      Response<TProvider>,
      { provider: TCreateProviderBody }
    >({
      invalidatesTags: ['Provider'],
      query: ({ provider }) => ({
        url: '',
        method: 'POST',
        body: provider,
      }),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TProvider>(
          res as JsonApiResponseDto<TProvider>,
          meta,
          args,
          true,
        ),
    }),
    updateProvider: builder.mutation<
      Response<TProvider>,
      { id: string; provider: TUpdateProviderBody }
    >({
      invalidatesTags: ['Provider'],
      query: ({ id, provider }) => ({
        url: `${id}`,
        method: 'PATCH',
        body: provider,
      }),
      transformResponse: (res, meta, args) =>
        parseJsonApiResponse<TProvider>(
          res as JsonApiResponseDto<TProvider>,
          meta,
          args,
          true,
        ),
    }),
  }),
});

export const {
  useGetProvidersQuery,
  useGetProviderQuery,
  useCreateProviderMutation,
  useUpdateProviderMutation,
} = providersApi;
